import React, { useContext, useEffect } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import SiteContext from 'site/SiteContext';
import { Loading } from 'dma-ui-kit';
import { DrupalPageContent } from './DrupalPage';

import createAxiosInstance from 'helpers/axios';
const axiosInstance = createAxiosInstance();

const appEnv = process.env.REACT_APP_ENV;
const baseEndpoint = process.env.REACT_APP_CMS_BASE_PATH;

// Matches the token format written by the Drupal jsonapi_to_s3 module
const LANG_PATTERN = /^(en|es)$/;
const TOKEN_PATTERN = /^[a-f0-9]{32}$/;

const Banner = styled.div`
    position: sticky;
    top: 0;
    z-index: 1000;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 24px;
    padding: 8px 16px;
    background: #fcd34d;
    color: #000;
    font-size: 14px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    a {
        color: #000;
        text-decoration: underline;
        margin-left: 16px;
    }
`;

const Unavailable = styled.div`
    padding: 60px 20px;
    text-align: center;
`;

/**
 * CMS preview. The Drupal Preview button writes the unsaved node to
 * content/preview/{lang}/{token}.json and sends the editor to
 * /preview/{lang}/{token}. This loads that manifest and renders it through
 * the same page components as published content, with an editor banner.
 */
const PreviewPage = (props) => {

    const { lang, token } = props.match.params;
    const { displayLanguage, setDisplayLanguage } = useContext(SiteContext);

    const valid = LANG_PATTERN.test(lang) && TOKEN_PATTERN.test(token);

    // Show the site chrome (menus, translations) in the language being previewed
    useEffect(() => {
        if (valid && lang !== displayLanguage)
            setDisplayLanguage(lang);
    }, [valid, lang, displayLanguage, setDisplayLanguage]);

    const endpoint = `${baseEndpoint}${appEnv}/content/preview/${lang}/${token}.json`;

    const { isLoading, error, data } = useQuery(['drupalPreview', endpoint], async () => 
        await axiosInstance.get(endpoint), { enabled: valid, cacheTime: 0, staleTime: 0 }
    );

    if (!valid)
        return <Unavailable>Invalid preview link.</Unavailable>;

    if (isLoading) return <Loading />;

    if (error || !data || !data.data)
        return <Unavailable>This preview is no longer available. Return to Drupal and click Preview again.</Unavailable>;

    const preview = data.preview || {};
    const pathname = preview.alias || data.data.attributes?.path?.alias || '';
    const generated = preview.generated ? new Date(preview.generated) : null;
    const generatedLabel = (generated && !isNaN(generated.getTime())) ?
        generated.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '';

    return (
        <>
            <Banner role="status">
                <span>
                    <strong>PREVIEW</strong>&nbsp; Unpublished changes to <strong>{preview.title || preview.node_type || 'this page'}</strong>
                    {' '}({lang.toUpperCase()}){generatedLabel ? `, generated ${generatedLabel}` : ''}. Only you can see this.
                </span>
                <span>
                    {preview.back_url && <a href={preview.back_url}>Back to editing</a>}
                    {pathname && <a href={pathname}>View published page</a>}
                </span>
            </Banner>
            <DrupalPageContent data={data} pathname={pathname} />
        </>
    );
};

export default PreviewPage;
